import { useState } from 'react';
import { trpc } from '@/lib/trpc';
import {
  AdminTable, AdminSectionHeader, inputCls, labelCls, tdCls, InlineError,
} from '@/components/admin/AdminShared';

// The five bundled preset icons (served from client/public/instructors/).
const PRESET_ICONS = [
  '/instructors/sunglasses.png',
  '/instructors/glasses.png',
  '/instructors/tie.png',
  '/instructors/mustache.png',
  '/instructors/round-glasses.png',
];

function fileToBase64(file: File): Promise<{ base64: string; contentType: string }> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;          // data:<type>;base64,<data>
      resolve({ base64: result.split(',')[1] ?? '', contentType: file.type || 'image/png' });
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function IconPicker({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const upload = trpc.instructors.uploadIcon.useMutation();
  const [busy, setBusy] = useState(false);

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const { base64, contentType } = await fileToBase64(file);
      const res = await upload.mutateAsync({ filename: file.name, dataBase64: base64, contentType });
      onChange(res.url);
    } catch (err: any) {
      alert(`Upload failed: ${err?.message ?? 'unknown error'}`);
    } finally {
      setBusy(false);
      e.target.value = '';
    }
  };

  return (
    <div>
      <label className={labelCls}>Icon</label>
      <div className="flex items-center gap-3 flex-wrap">
        {PRESET_ICONS.map(p => (
          <button type="button" key={p} onClick={() => onChange(p)} title="Use this preset"
                  className={`w-14 h-14 rounded-2xl border-2 overflow-hidden bg-[#0d0d10] transition-colors
                              ${value === p ? 'border-yellow-400' : 'border-white/10 hover:border-white/30'}`}>
            <img src={p} alt="" className="w-full h-full object-contain" />
          </button>
        ))}
        <label className="w-14 h-14 rounded-2xl border-2 border-dashed border-white/15 hover:border-yellow-400/50
                          flex items-center justify-center cursor-pointer text-[#71717A] text-[10px] font-bold transition-colors">
          {busy ? '…' : 'Upload'}
          <input type="file" accept="image/*" className="hidden" onChange={onFile} disabled={busy} />
        </label>
      </div>
      {value && !PRESET_ICONS.includes(value) && (
        <div className="mt-3 flex items-center gap-2">
          <img src={value} alt="" className="w-10 h-10 rounded-xl object-contain bg-[#0d0d10] border border-white/10" />
          <button type="button" onClick={() => onChange('')}
                  className="text-[#52525B] hover:text-red-400 text-xs">clear custom icon</button>
        </div>
      )}
    </div>
  );
}

const EMPTY = { name: '', iconUrl: '', bio: '', coursePercentage: '100', sessionPercentage: '100' };

export default function AdminInstructors() {
  const utils = trpc.useUtils();
  const { data: instructors = [] } = trpc.instructors.list.useQuery();
  const refresh = () => utils.instructors.list.invalidate();

  const [form, setForm]       = useState(EMPTY);
  const [editId, setEditId]   = useState<number | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const reset = () => { setForm(EMPTY); setEditId(null); };

  const create = trpc.instructors.create.useMutation({ onSuccess: () => { refresh(); reset(); } });
  const update = trpc.instructors.update.useMutation({ onSuccess: () => { refresh(); reset(); } });
  const del    = trpc.instructors.delete.useMutation({ onSuccess: () => refresh() });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    const clampPct = (v: string) => Math.max(0, Math.min(100, Number(v) || 0));
    const payload = {
      name:    form.name.trim(),
      iconUrl: form.iconUrl.trim() || null,
      bio:     form.bio.trim() || null,
      coursePercentage:  clampPct(form.coursePercentage),
      sessionPercentage: clampPct(form.sessionPercentage),
    };
    if (editId != null) update.mutate({ id: editId, ...payload });
    else                create.mutate({ ...payload, order: instructors.length });
  };

  const startEdit = (i: typeof instructors[number]) => {
    setEditId(i.id);
    setForm({
      name: i.name,
      iconUrl: (i as any).iconUrl ?? '',
      bio: (i as any).bio ?? '',
      coursePercentage:  String((i as any).coursePercentage  ?? '100'),
      sessionPercentage: String((i as any).sessionPercentage ?? '100'),
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const pending = create.isPending || update.isPending;
  const err = create.error ?? update.error;

  return (
    <div>
      <AdminSectionHeader title="Instructors" count={instructors.length} />

      {/* Create / edit form */}
      <form onSubmit={submit}
            className="bg-[#09090B] border border-yellow-400/20 border-dashed rounded-3xl p-6 mb-8 space-y-4">
        <h3 className="text-base font-black text-white">
          {editId != null ? 'Edit Instructor' : 'New Instructor'}
        </h3>

        <div>
          <label className={labelCls}>Name *</label>
          <input value={form.name} required
                 onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                 placeholder="e.g. Dr. Ahmad" className={inputCls} />
        </div>

        <IconPicker value={form.iconUrl} onChange={v => setForm(f => ({ ...f, iconUrl: v }))} />

        <div>
          <label className={labelCls}>Bio</label>
          <textarea rows={2} value={form.bio}
                    onChange={e => setForm(f => ({ ...f, bio: e.target.value }))}
                    placeholder="Optional short bio" className={`${inputCls} resize-none`} />
        </div>

        {/* Revenue share */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Course sales cut (%)</label>
            <input type="number" min={0} max={100} step="0.01" value={form.coursePercentage}
                   onChange={e => setForm(f => ({ ...f, coursePercentage: e.target.value }))}
                   className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Online session cut (%)</label>
            <input type="number" min={0} max={100} step="0.01" value={form.sessionPercentage}
                   onChange={e => setForm(f => ({ ...f, sessionPercentage: e.target.value }))}
                   className={inputCls} />
          </div>
        </div>
        <p className="text-[#52525B] text-xs -mt-2">
          The instructor's dashboard shows only this share of the money. 100% = they keep everything.
        </p>

        {err && <InlineError message={err.message} />}

        <div className="flex gap-3">
          <button type="submit" disabled={pending}
                  className="bg-yellow-400 text-black font-black px-6 py-2.5 rounded-xl text-sm
                             transition-colors disabled:opacity-50">
            {pending ? 'Saving…' : editId != null ? 'Save Changes' : 'Add Instructor'}
          </button>
          {editId != null && (
            <button type="button" onClick={reset}
                    className="px-5 py-2.5 border border-white/10 text-[#A1A1AA] hover:text-white
                               rounded-xl text-sm transition-colors">
              Cancel
            </button>
          )}
        </div>
      </form>

      {/* List */}
      <AdminTable headers={['Instructor', 'Bio', 'Cut (course / session)', 'Actions']} empty={instructors.length === 0}>
        {instructors.map(i => (
          <tr key={i.id} className="border-t border-white/[0.04]">
            <td className={tdCls}>
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-2xl bg-[#0d0d10] border border-white/10 overflow-hidden shrink-0">
                  {(i as any).iconUrl
                    ? <img src={(i as any).iconUrl} alt="" className="w-full h-full object-contain" />
                    : <div className="w-full h-full flex items-center justify-center text-[#3F3F46] text-lg">?</div>}
                </div>
                <span className="text-white font-semibold text-sm">{i.name}</span>
              </div>
            </td>
            <td className={`${tdCls} text-[#71717A] text-xs max-w-[280px] truncate`}>
              {(i as any).bio || '—'}
            </td>
            <td className={`${tdCls} text-[#A1A1AA] text-xs whitespace-nowrap`}>
              {Math.round(Number((i as any).coursePercentage  ?? 100))}% / {Math.round(Number((i as any).sessionPercentage ?? 100))}%
            </td>
            <td className={tdCls}>
              <div className="flex gap-2 items-center flex-wrap">
                <button onClick={() => startEdit(i)}
                        className="text-xs text-[#A1A1AA] hover:text-white border border-white/10
                                   hover:border-white/20 px-3 py-1.5 rounded-lg transition-colors">
                  Edit
                </button>
                {deleteId === i.id ? (
                  <div className="flex gap-1.5 items-center">
                    <span className="text-[#71717A] text-xs">Sure?</span>
                    <button onClick={() => { del.mutate({ id: i.id }); setDeleteId(null); }}
                            className="text-xs text-red-400 border border-red-400/30 px-2.5 py-1 rounded-lg
                                       hover:bg-red-400/10 transition-colors">Yes</button>
                    <button onClick={() => setDeleteId(null)}
                            className="text-xs text-[#71717A] border border-white/10 px-2.5 py-1 rounded-lg
                                       hover:text-white transition-colors">No</button>
                  </div>
                ) : (
                  <button onClick={() => setDeleteId(i.id)}
                          className="text-xs text-red-400 border border-red-400/20 hover:border-red-400/40
                                     px-3 py-1.5 rounded-lg transition-colors">
                    Delete
                  </button>
                )}
              </div>
            </td>
          </tr>
        ))}
      </AdminTable>
      <p className="text-[#52525B] text-xs mt-3">
        Removing an instructor unlinks their courses and sessions — it doesn't delete them.
      </p>
    </div>
  );
}
